import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Crear instancia de axios
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Interceptor para agregar token a todas las peticiones
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
            // Token expirado o inválido (pero no en el intento de login)
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Servicios de autenticación
export const authService = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (userData) => api.post('/auth/register', userData),
    getProfile: () => api.get('/auth/me'),
    updateProfile: (profileData) => api.put('/auth/profile', profileData),
    uploadProfilePhoto: (file) => {
        const formData = new FormData();
        formData.append('profile_image', file);
        return api.post('/auth/profile/photo', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    deleteProfilePhoto: () => api.delete('/auth/profile/photo'),
    changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { currentPassword, newPassword }),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
    getUsers: () => api.get('/auth/users'),
    createUser: (userData) => api.post('/auth/users', userData),
    updateUser: (id, userData) => api.put(`/auth/users/${id}`, userData),
    deleteUser: (id) => api.delete(`/auth/users/${id}`),
    updatePreferences: (preferences) => api.put('/auth/preferences', { preferences })
};

// Servicios de blog
export const blogService = {
    getAll: (params) => api.get('/blog/dashboard/all', { params }),
    getPosts: (params) => api.get('/blog', { params }),
    getPost: (slug) => api.get(`/blog/slug/${slug}`),
    getById: (id) => api.get(`/blog/id/${id}`),
    create: (postData) => {
        return api.post('/blog', postData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    update: (id, postData) => {
        return api.put(`/blog/${id}`, postData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    delete: (id) => api.delete(`/blog/${id}`),
    // Métodos legacy
    getDashboardPosts: (params) => api.get('/blog/dashboard/all', { params }),
    createPost: (postData) => {
        return api.post('/blog', postData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    updatePost: (id, postData) => {
        return api.put(`/blog/${id}`, postData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    deletePost: (id) => api.delete(`/blog/${id}`)
};

// Servicios de eventos
export const eventsService = {
    getAll: () => api.get('/events/dashboard/all'),
    getEvents: (params) => api.get('/events', { params }),
    getEvent: (id) => api.get(`/events/${id}`),
    create: (eventData) => {
        return api.post('/events', eventData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    update: (id, eventData) => {
        return api.put(`/events/${id}`, eventData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    delete: (id) => api.delete(`/events/${id}`)
};

// Servicios de convocatorias
export const announcementsService = {
    getAll: () => api.get('/announcements/dashboard/all'),
    getAnnouncements: (params) => api.get('/announcements', { params }),
    getAnnouncement: (id) => api.get(`/announcements/${id}`),
    create: (announcementData) => {
        return api.post('/announcements', announcementData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    update: (id, announcementData) => {
        return api.put(`/announcements/${id}`, announcementData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    delete: (id) => api.delete(`/announcements/${id}`),
    // Métodos legacy (mantener por compatibilidad)
    getDashboardAnnouncements: () => api.get('/announcements/dashboard/all'),
    createAnnouncement: (data) => api.post('/announcements', data),
    updateAnnouncement: (id, data) => api.put(`/announcements/${id}`, data),
    deleteAnnouncement: (id) => api.delete(`/announcements/${id}`)
};

// Servicios de banners
export const bannersService = {
    getAll: () => api.get('/banners/dashboard/all'),
    getBanners: () => api.get('/banners'),
    getBanner: (id) => api.get(`/banners/${id}`),
    create: (bannerData) => {
        return api.post('/banners', bannerData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    update: (id, bannerData) => {
        return api.put(`/banners/${id}`, bannerData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    delete: (id) => api.delete(`/banners/${id}`),
    // Métodos legacy
    getDashboardBanners: () => api.get('/banners/dashboard/all'),
    createBanner: (bannerData) => api.post('/banners', bannerData),
    updateBanner: (id, bannerData) => api.put(`/banners/${id}`, bannerData),
    deleteBanner: (id) => api.delete(`/banners/${id}`)
};

// Servicios de categorías
export const categoriesService = {
    getAll: () => api.get('/categories/dashboard/all'),
    getCategories: (params) => api.get('/categories', { params }),
    getCategory: (id) => api.get(`/categories/${id}`),
    create: (categoryData) => api.post('/categories', categoryData),
    update: (id, categoryData) => api.put(`/categories/${id}`, categoryData),
    delete: (id) => api.delete(`/categories/${id}`),
    // Métodos legacy
    getDashboardCategories: () => api.get('/categories/dashboard/all'),
    createCategory: (categoryData) => api.post('/categories', categoryData),
    updateCategory: (id, categoryData) => api.put(`/categories/${id}`, categoryData),
    deleteCategory: (id) => api.delete(`/categories/${id}`)
};

// Servicios de upload
export const uploadService = {
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    uploadDocument: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/document', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }
};

// Servicios de roles personalizados
export const rolesService = {
    getAll: () => api.get('/roles'),
    getRole: (id) => api.get(`/roles/${id}`),
    create: (roleData) => api.post('/roles', roleData),
    update: (id, roleData) => api.put(`/roles/${id}`, roleData),
    delete: (id) => api.delete(`/roles/${id}`)
};

export default api;
